import { PositionedSpinner } from "@flightpath/coreui/dist/ui/PositionedSpinner";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IF } from "../../../../../components/hoc/If";

import { Panel } from "../../../../../components/ui/Panel";
import { BarChart } from "../../../../../core/charts/BarChart/BarChart";
import {
  CommonColDefFieldNamesEnum,
  TrainingModuleColDefFieldNamesEnum
} from "../../../../../core/grids/builder/enums/AgGridColDefFieldNameEnum";
import I18n from "../../../../../core/localization/I18n";
import { ReportFilterView } from "../../../../../core/reportFilter/ReportFilter_view";
import { ReportFilterAddonsKeys } from "../../../../../core/reportFilter/ReportFilterAddon";
import Pages from "../../../../../routes/InsightRoutes";
import { useCurrentOrganisationId } from "../../../../../services/local/organisationContext/OrganisationContextModel";
import { TrainingModulesReportingModel } from "./TrainingModulesReportingView_model";
import { PROGRESS_STATUS_COLORS } from "../../../../../core/util/VisualisationsHelpers";
import { PROGRESS_STATUS_OPTIONS, RAG_STATUS_OPTIONS } from "../../../../../constants";

export interface TrainingModulesReportingViewProps {}

export const TrainingModulesReportingView: React.FC<TrainingModulesReportingViewProps> = observer(() => {
  const navigate = useNavigate();
  const organisationId = useCurrentOrganisationId();
  const { projectId, page } = useParams<{ projectId: string; page: string }>();
  const [model, setModel] = useState(
    () => new TrainingModulesReportingModel(organisationId, +projectId, navigate, page)
  );
  const modelProjectId = model.projectId;
  const progressStatusColors = [
    PROGRESS_STATUS_COLORS.NOT_STARTED,
    PROGRESS_STATUS_COLORS.IN_PROGRESS,
    PROGRESS_STATUS_COLORS.COMPLETED,
    PROGRESS_STATUS_COLORS.ON_HOLD,
    PROGRESS_STATUS_COLORS.CANCELLED
  ];
  const ownerChartData = model.ownerChartData;
  const trainingProgressData = model.trainingProgressData;

  useEffect(() => {
    model.onMount();
    return model.onUnmount;
  }, [model]);

  useEffect(() => {
    if (`${modelProjectId}` !== projectId) {
      setModel(new TrainingModulesReportingModel(organisationId, +projectId, navigate, page));
    }
  }, [projectId, modelProjectId, organisationId, model, navigate, page]);

  const DrillThroughOnClickModulesByOwner =
    (organisationId, projectId, navigate, hasFilters: boolean) => (dataObject: any) => {
      if (!hasFilters) {
        let url = "";
        const base = Pages.projects.training.modules.generateLink(organisationId, projectId);
        url = `${base}?${TrainingModuleColDefFieldNamesEnum.Owner}=${dataObject.indexValue}`;
        navigate(url);
      }
    };

  const DrillThroughOnClickModulesByTrainingProgress =
    (organisationId, projectId, navigate, hasFilters: boolean) => (dataObject: any) => {
      if (!hasFilters) {
        let url = "";
        const base = Pages.projects.training.modules.generateLink(organisationId, projectId);
        url = `${base}?${TrainingModuleColDefFieldNamesEnum.TrainingProgress}=${dataObject.indexValue}`;
        navigate(url);
      }
    };

  const DrillThroughOnClickModulesByActionProgressStatus =
    (organisationId, projectId, navigate, hasFilters) => (dataObject: any) => {
      let filteredActionRefNumbers: any = [];
      const data = dataObject.data;
      const filteredProgressStatusKey = PROGRESS_STATUS_OPTIONS.find(p => I18n.t(p.label) === dataObject.id)?.key;
      // eslint-disable-next-line
      data.actions.map(action => {
        if (action.progressStatus === filteredProgressStatusKey) {
          filteredActionRefNumbers.push(`${action.refNumber}`);
        }
      });

      if (!hasFilters) {
        const base = Pages.projects.actions.listView.generateLink(organisationId, projectId);
        const url = `${base}?${CommonColDefFieldNamesEnum.RefNumber}=${filteredActionRefNumbers.join("|")}`;
        navigate(url);
      }
    };

  const DrillThroughOnClickModulesByActionRagStatus =
    (organisationId, projectId, navigate, hasFilters) => (dataObject: any) => {
      const data = dataObject.data;

      const getFilteredActions = () => {
        switch (dataObject.id) {
          case "Completed":
            return data.actions.filter(action => action.progressStatus === 3);

          case "Unknown":
            return data.actions.filter(action => action.ragStatus === null);

          default: {
            const filteredRagStatusKey = RAG_STATUS_OPTIONS.find(p => I18n.t(p.label) === dataObject.id)?.key;
            return data.actions.filter(
              action => action.ragStatus === filteredRagStatusKey && action.progressStatus !== 3
            );
          }
        }
      };

      const filteredActionRefNumbers = getFilteredActions().map(action => action.refNumber);

      if (!hasFilters) {
        const base = Pages.projects.actions.listView.generateLink(organisationId, projectId);
        const url = `${base}?${CommonColDefFieldNamesEnum.RefNumber}=${filteredActionRefNumbers.join("|")}`;
        navigate(url);
      }
    };

  if (model.isLoading) return <PositionedSpinner />;

  return (
    <>
      <ReportFilterView
        addons={[ReportFilterAddonsKeys.CoursesGrid]}
        filterState={model.filterJSON}
        onClearClicked={model.resetLocalStorage}
        onFilterChange={async k => {
          model.setFilterJSONFromList(k);

          return await Promise.all([model.loadData()]).then(async e => {
            return null;
          });
        }}
      />
      <hr className="m-0" />
      <div className="container-fluid py-5">
        <div className="row mb-4">
          <div className="col-12 col-md-6 col-lg-6 col-xxl-6 mb-5 px-3 p-0">
            {/*Bar Chart by Mdule Owner*/}
            <Panel.Panel hasBorder hasBorderRadius background={Panel.PanelBackgrounds.BG_LIGHT} className="p-3 mt-0">
              <h3>{I18n.t("visualisations.modulesByOwner")}</h3>
              <div className="training-course-owner-chart__wrapper">
                <IF condition={!model.isLoading && ownerChartData.length === 0}>
                  <div className="training-course-owner-chart__data-wrapper">
                    <p className="mb-0">{I18n.t("phrases.noModulesByOwner")}</p>
                  </div>
                </IF>
                <IF condition={!model.isLoading && ownerChartData.length > 0}>
                  <BarChart
                    data={ownerChartData}
                    keys={["count"]}
                    indexBy={"label"}
                    colors={["#19d3e0"]}
                    tooltipLabelFn={({ indexValue }) => `${indexValue}`}
                    leftAxisLabel="Number of Modules"
                    onClick={DrillThroughOnClickModulesByOwner(organisationId, projectId, navigate, model.hasFilters)}
                    legendLabel={d => I18n.t(`phrases.${d.id}`)}
                    chartMargin={{ top: 20, right: 100, bottom: 55, left: 50 }}
                    bottomAxisValueFormat={(val: string) => ({
                      value: val,
                      maxLineLength: 15,
                      maxLines: 2,
                      truncateAtChar: 15
                    })}
                  />
                </IF>
              </div>
            </Panel.Panel>
          </div>
          <div className="col-12 col-md-6 col-lg-6 col-xxl-6 mb-5 px-3 p-0">
            {/*Bar Chart by Mdule Training Progress*/}
            <Panel.Panel hasBorder hasBorderRadius background={Panel.PanelBackgrounds.BG_LIGHT} className="p-3 mt-0">
              <h3>{I18n.t("visualisations.modulesByTrainingProgress")}</h3>
              <div className="training-course-owner-chart__wrapper">
                <IF condition={!model.isLoading && trainingProgressData.length === 0}>
                  <div className="training-course-owner-chart__data-wrapper">
                    <p className="mb-0">{I18n.t("phrases.noModulesByTrainingProgress")}</p>
                  </div>
                </IF>
                <IF condition={!model.isLoading && trainingProgressData.length > 0}>
                  <BarChart
                    data={trainingProgressData}
                    keys={["count"]}
                    indexBy={"label"}
                    colors={["#19d3e0"]}
                    tooltipLabelFn={({ indexValue }) => `${indexValue}`}
                    leftAxisLabel="Number of Modules"
                    onClick={DrillThroughOnClickModulesByTrainingProgress(
                      organisationId,
                      projectId,
                      navigate,
                      model.hasFilters
                    )}
                    chartMargin={{ top: 20, right: 100, bottom: 55, left: 50 }}
                    legendLabel={d => I18n.t(`phrases.${d.id}`)}
                    bottomAxisValueFormat={(val: string) => ({
                      value: val,
                      maxLineLength: 15,
                      maxLines: 2,
                      truncateAtChar: 15
                    })}
                  />
                </IF>
              </div>
            </Panel.Panel>
          </div>
          <div className="col-12 col-md-6 col-lg-6 col-xxl-6 mb-5 px-3 p-0">
            <Panel.Panel hasBorder hasBorderRadius background={Panel.PanelBackgrounds.BG_LIGHT} className="p-3 mt-0">
              <h3>{I18n.t("visualisations.modulesByActionProgressStatus")}</h3>
              <div className="training-course-owner-chart__wrapper">
                <IF condition={!model.isLoading && model.trainingModuleActionProgressStatus.length === 0}>
                  <div className="training-course-owner-chart__data-wrapper">
                    <p className="mb-0">{I18n.t("visualisations.noModulesByActionProgressStatus")}</p>
                  </div>
                </IF>
                <IF condition={!model.isLoading && model.trainingModuleActionProgressStatus.length > 0}>
                  <BarChart
                    data={model.trainingModuleActionProgressStatus}
                    keys={["Not Started", "In Progress", "Completed"]}
                    colors={progressStatusColors}
                    indexBy="trainingModule"
                    onClick={DrillThroughOnClickModulesByActionProgressStatus(
                      organisationId,
                      projectId,
                      navigate,
                      model.hasFilters
                    )}
                    tooltipLabelFn={({ indexValue }) => `${indexValue}`}
                    chartMargin={{ top: 20, right: 102, bottom: 55, left: 50 }}
                    bottomAxisValueFormat={(val: string) => ({
                      value: val,
                      maxLineLength: 15,
                      maxLines: 2,
                      truncateAtChar: 15
                    })}
                    legendLabel={d => d.id}
                    leftAxisLabel="Action Count"
                  />
                </IF>
              </div>
            </Panel.Panel>
          </div>
          <div className="col-12 col-md-6 col-lg-6 col-xxl-6 mb-5 px-3 p-0">
            <Panel.Panel hasBorder hasBorderRadius background={Panel.PanelBackgrounds.BG_LIGHT} className="p-3 mt-0">
              <h3>{I18n.t("visualisations.modulesByActionRagStatus")}</h3>
              <div className="training-course-owner-chart__wrapper">
                <IF condition={!model.isLoading && model.trainingModuleActionRagStatus.length === 0}>
                  <div className="training-course-owner-chart__data-wrapper">
                    <p className="mb-0">{I18n.t("visualisations.noModulesByActionRagStatus")}</p>
                  </div>
                </IF>
                <IF condition={!model.isLoading && model.trainingModuleActionRagStatus.length > 0}>
                  <BarChart
                    data={model.trainingModuleActionRagStatus}
                    keys={["Unknown", "Red", "Amber", "Green", "Completed"]}
                    colors={["#E5E5E5", "#F91E1E", "#F9AB1E", "#1AC541", "#58C8D1"]}
                    onClick={DrillThroughOnClickModulesByActionRagStatus(
                      organisationId,
                      projectId,
                      navigate,
                      model.hasFilters
                    )}
                    indexBy="trainingModule"
                    tooltipLabelFn={({ indexValue }) => `${indexValue}`}
                    chartMargin={{ top: 20, right: 100, bottom: 55, left: 50 }}
                    bottomAxisValueFormat={(val: string) => ({
                      value: val,
                      maxLineLength: 15,
                      maxLines: 2,
                      truncateAtChar: 15
                    })}
                    legendLabel={d => d.id}
                    leftAxisLabel="Action Count"
                  />
                </IF>
              </div>
            </Panel.Panel>
          </div>
        </div>
      </div>
    </>
  );
});
